import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface Competency {
  id: string;
  name: string;
  slug: string;
  dimension: 'technical' | 'behavioral';
  description: string;
  sort_order: number;
}

interface ForburyLevel {
  competency_id: string;
  level: 'junior' | 'intermediate' | 'senior' | 'lead';
  description: string;
}

// Admin view of the Forbury Competency Framework. Read-only reference
// data: which competencies exist, what they cover, and the framework's
// career-level descriptors (junior/intermediate/senior/lead).
//
// IMPORTANT: these career-level names are FRAMEWORK REFERENCE ONLY.
// They describe real-world career stages and are NOT used as game
// levels. The player-facing skill tree uses RPG tiers (Novice/
// Apprentice/Adept/Expert/Master) — see ADR-0008. This page is the
// only place the Forbury career-level names appear in the UI, and
// it's admin-only by design.
export function AdminCompetenciesPage() {
  const { data, isLoading, error } = useQuery<{ competencies: Competency[] }>({
    queryKey: ['competencies'],
    queryFn: () => api('/api/competencies'),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Competencies</h1>
        <p className="text-ink-muted">
          Forbury framework reference. Read-only. The career-level names below
          (junior/intermediate/senior/lead) are framework descriptors — they are
          NOT the game tiers players see. Players progress through RPG tiers
          (Novice → Master) on the skill tree.
        </p>
      </header>

      {isLoading ? <p className="text-ink-muted">Loading…</p> : null}
      {error ? <p className="text-accent-danger">Failed to load competencies.</p> : null}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {(data?.competencies ?? []).map((c) => (
          <CompetencyCard key={c.id} competency={c} />
        ))}
      </div>
    </div>
  );
}

function CompetencyCard({ competency }: { competency: Competency }) {
  const [expanded, setExpanded] = useState(false);
  const levelsQuery = useQuery<{ levels: ForburyLevel[] }>({
    queryKey: ['competency-levels', competency.id],
    queryFn: () => api(`/api/competencies/${competency.id}/levels`),
    enabled: expanded,
    staleTime: 5 * 60_000,
  });

  return (
    <article className="card space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">{competency.name}</h2>
        <span className={`rounded-full px-2 py-0.5 text-xs ${competency.dimension === 'technical' ? 'bg-accent/15 text-accent' : 'bg-accent-success/15 text-accent-success'}`}>
          {competency.dimension}
        </span>
      </div>
      <p className="text-sm text-ink-muted">{competency.description}</p>
      <p className="font-mono text-xs text-ink-dim">{competency.slug}</p>
      <button
        className="text-xs text-ink-muted underline hover:text-ink"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? 'Hide framework levels' : 'Show framework levels (career reference)'}
      </button>
      {expanded ? (
        levelsQuery.isLoading ? (
          <p className="text-xs text-ink-muted">Loading…</p>
        ) : levelsQuery.error ? (
          <p className="text-xs text-accent-danger">Failed to load levels.</p>
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-ink-dim">
                <th className="py-1 pr-2 font-medium">Career level</th>
                <th className="py-1 font-medium">Description (framework reference)</th>
              </tr>
            </thead>
            <tbody>
              {(levelsQuery.data?.levels ?? []).map((l) => (
                <tr key={l.level} className="align-top">
                  <td className="py-1 pr-2 font-medium capitalize">{l.level}</td>
                  <td className="py-1 text-ink-muted">{l.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </article>
  );
}